import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import Loader from '../../layout/Loder';
import { logout, deleteAccount, getReferralInfo } from '../../actions/userAction';
import './Account.css';

const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Account = () => {
  const { user, loading, isAuthenticated } = useSelector((state) => state.user);
  const referral = useSelector((state) => state.referral);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) dispatch(getReferralInfo());
  }, [isAuthenticated, dispatch]);

  // window.location.origin (not config.apiUrl, that's the backend) so this
  // works correctly on both localhost and dmyor.com without extra config.
  const referralLink = referral.referralCode ? `${window.location.origin}/signup?ref=${referral.referralCode}` : '';

  const copyReferralLink = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2200);
    } catch (_) {}
  };

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      setLogoutLoading(true);
      await dispatch(logout());
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Logout failed');
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await dispatch(deleteAccount());
      toast.success('Your account has been deleted.');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not delete account. Try again.');
      setDeleting(false);
    }
  };

  if (loading || !user) return <Loader />;

  const joined = user.createdAt ? moment(user.createdAt) : null;
  const dob = user.dob ? moment(user.dob).format('DD MMM YYYY') : null;

  return (
    <>
      <Helmet><title>Account · dmyor</title></Helmet>

      <div className="acc-page">
        <div className="acc-wrap">
          <header className="acc-header">
            <div className="acc-avatar" aria-hidden="true">
              {initialsOf(user.name)}
            </div>
            <div className="acc-identity">
              <h1 className="acc-name">{user.name || 'Unnamed'}</h1>
              <p className="acc-email">{user.email}</p>
              {joined && (
                <p className="acc-joined">
                  On dmyor since {joined.format('MMM YYYY')}
                </p>
              )}
            </div>
          </header>

          <section className="acc-card">
            <div className="acc-card-title">Details</div>

            <div className="acc-row">
              <span className="acc-row-label">Name</span>
              <span className="acc-row-value">{user.name || '—'}</span>
            </div>

            <div className="acc-row">
              <span className="acc-row-label">Email</span>
              <span className="acc-row-value">{user.email}</span>
            </div>

            {dob && (
              <div className="acc-row">
                <span className="acc-row-label">Date of birth</span>
                <span className="acc-row-value">{dob}</span>
              </div>
            )}

            {joined && (
              <div className="acc-row">
                <span className="acc-row-label">Member since</span>
                <span className="acc-row-value">{joined.format('DD MMM YYYY')}</span>
              </div>
            )}
          </section>

          <section className="acc-card acc-reward-card">
            <div className="acc-card-title">Reward Programme</div>

            <div className="acc-reward-top">
              <div className="acc-reward-points">
                <span className="acc-reward-points-num">{referral.rewardPoints || 0}</span>
                <span className="acc-reward-points-label">reward points</span>
              </div>
              <p className="acc-reward-note">
                Redeemable for AI features once Phase 2 ships. Points don't expire.
              </p>
            </div>

            <div className="acc-reward-steps">
              <div className="acc-reward-step">
                <span className="acc-reward-step-num">1</span>
                Share your link
              </div>
              <div className="acc-reward-step">
                <span className="acc-reward-step-num">2</span>
                A friend joins dmyor and creates their first vision
              </div>
              <div className="acc-reward-step">
                <span className="acc-reward-step-num">3</span>
                You earn 150 points
              </div>
            </div>

            {referral.referralCode && (
              <div className="acc-reward-link-row">
                <input
                  className="acc-reward-link-input"
                  readOnly
                  value={referralLink}
                  onFocus={(e) => e.target.select()}
                />
                <button type="button" className="acc-manage-btn" onClick={copyReferralLink}>
                  {linkCopied ? 'Copied ✓' : 'Copy link'}
                </button>
              </div>
            )}

            {referral.referrals && referral.referrals.length > 0 && (
              <div className="acc-reward-history">
                <div className="acc-reward-history-title">
                  Your referrals ({referral.referralActivatedCount || 0}/{referral.maxRewardedReferrals || 20} rewarded)
                </div>
                {referral.referrals.map((r, i) => (
                  <div className="acc-reward-history-row" key={i}>
                    <span className="acc-reward-history-name">{r.name}</span>
                    <span className={`acc-reward-history-status acc-reward-history-status--${r.status}`}>
                      {r.status === 'activated' ? `+${r.pointsAwarded} pts` : r.status === 'capped' ? 'Cap reached' : 'Joined, waiting on their first vision'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button type="button" className="acc-reward-full-link" onClick={() => navigate('/dashboard/refer')}>
              View full referral program →
            </button>
          </section>

          <section className="acc-sub-card">
            <div className="acc-sub-icon" aria-hidden="true">
              <i className="bx bxs-bolt" />
            </div>
            <div className="acc-sub-text">
              <p className="acc-sub-title">dmyor Pro</p>
              <p className="acc-sub-desc">Your free AI requests, plan, billing, and cancellation.</p>
            </div>
            <button type="button" className="acc-manage-btn" onClick={() => navigate('/dashboard/upgrade')}>
              Manage
            </button>
          </section>

          <section className="acc-sub-card">
            <div className="acc-sub-icon" aria-hidden="true">
              <i className="bx bx-envelope" />
            </div>
            <div className="acc-sub-text">
              <p className="acc-sub-title">Contact & support</p>
              <p className="acc-sub-desc">
                Questions, feedback, or a problem? Reach us at{' '}
                <a className="acc-sub-mail" href="mailto:support@dmyor.com">support@dmyor.com</a>.
              </p>
            </div>
            <a className="acc-manage-btn" href="mailto:support@dmyor.com">
              Email us
            </a>
          </section>

          <div className="acc-footer">
            <button
              type="button"
              className="acc-logout"
              onClick={handleLogout}
              disabled={logoutLoading}
            >
              <i className="bx bx-log-out" />
              {logoutLoading ? 'Logging out…' : 'Log out'}
            </button>
          </div>

          <section className="acc-card acc-card--danger">
            <div className="acc-card-title acc-card-title--danger">Danger zone</div>
            <div className="acc-danger-row">
              <div className="acc-danger-text">
                <p className="acc-danger-title">Delete account</p>
                <p className="acc-danger-desc">
                  Permanently deletes your account and all your data: visions, milestones,
                  actions, documents, habits, todos, and journal. This cannot be undone.
                </p>
              </div>
              <button type="button" className="acc-delete-btn" onClick={() => { setShowDelete(true); setConfirmText(''); }}>
                Delete account
              </button>
            </div>
          </section>
        </div>
      </div>

      {showDelete && (
        <div className="acc-modal-overlay" onMouseDown={() => !deleting && setShowDelete(false)}>
          <div className="acc-modal" onMouseDown={e => e.stopPropagation()}>
            <h3 className="acc-modal-title">Delete your account?</h3>
            <p className="acc-modal-text">
              This permanently erases your account and everything in it. There is no undo.
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              className="acc-modal-input"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
            <div className="acc-modal-actions">
              <button className="acc-modal-cancel" onClick={() => setShowDelete(false)} disabled={deleting}>Cancel</button>
              <button
                className="acc-modal-go"
                onClick={handleDelete}
                disabled={confirmText.trim().toUpperCase() !== 'DELETE' || deleting}
              >
                {deleting ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Account;
